import dayjs from 'dayjs';

export const DEFAULT_RANGE_DAYS = 30;

const STORAGE_START = 'globalStartDate';
const STORAGE_END = 'globalEndDate';

/** Default reporting window: last 30 calendar days through end of today. */
export function getDefaultDateRange(now = dayjs()) {
  const end = now.endOf('day');
  const start = now.subtract(DEFAULT_RANGE_DAYS, 'day').startOf('day');
  return { start, end };
}

export function normalizeRangeStart(value) {
  return dayjs(value).startOf('day');
}

export function normalizeRangeEnd(value) {
  return dayjs(value).endOf('day');
}

export function loadStoredDateRange() {
  const savedStart = localStorage.getItem(STORAGE_START);
  const savedEnd = localStorage.getItem(STORAGE_END);
  if (savedStart && savedEnd) {
    const start = normalizeRangeStart(savedStart);
    const end = normalizeRangeEnd(savedEnd);
    if (start.isValid() && end.isValid() && !start.isAfter(end)) {
      return { start, end };
    }
  }
  return getDefaultDateRange();
}

export function persistDateRange(start, end) {
  const s = normalizeRangeStart(start);
  const e = normalizeRangeEnd(end);
  localStorage.setItem(STORAGE_START, s.toISOString());
  localStorage.setItem(STORAGE_END, e.toISOString());
  return { start: s, end: e };
}

export function dispatchGlobalDateChange(start, end) {
  const { start: s, end: e } = persistDateRange(start, end);
  window.dispatchEvent(
    new CustomEvent('globalDateChange', {
      detail: {
        startDate: s.toISOString(),
        endDate: e.toISOString(),
      },
    })
  );
}

export function toRangeTimestamps(start, end) {
  const s = normalizeRangeStart(start);
  const e = normalizeRangeEnd(end);
  return {
    startMs: s.valueOf(),
    endMs: e.valueOf(),
    startIso: s.format('YYYY-MM-DD'),
    endIso: e.format('YYYY-MM-DD'),
    rangeKey: `${s.format('YYYY-MM-DD')}_${e.format('YYYY-MM-DD')}`,
  };
}

export function isTimestampInRange(ts, startMs, endMs) {
  return ts >= startMs && ts <= endMs;
}

export function parseOppDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** Infer quick-range preset from current start/end (±1 day tolerance). */
export function detectPresetRange(start, end) {
  const startDay = normalizeRangeStart(start);
  const endDay = normalizeRangeEnd(end);
  const diff = endDay.startOf('day').diff(startDay.startOf('day'), 'day');
  const today = dayjs().endOf('day');

  if (!endDay.isSame(today, 'day') && !endDay.isSame(dayjs(), 'day')) {
    return 'custom';
  }
  if (diff >= 29 && diff <= 31) return '30';
  if (diff >= 88 && diff <= 92) return '90';
  if (diff >= 178 && diff <= 182) return '180';
  if (diff >= 363 && diff <= 367) return '365';
  if (startDay.year() <= 1971) return 'all';
  return 'custom';
}

export function presetToDateRange(preset, now = dayjs()) {
  const end = now.endOf('day');
  switch (preset) {
    case '30':
      return { start: end.subtract(30, 'day').startOf('day'), end };
    case '90':
      return { start: end.subtract(90, 'day').startOf('day'), end };
    case '180':
      return { start: end.subtract(180, 'day').startOf('day'), end };
    case '365':
      return { start: end.subtract(365, 'day').startOf('day'), end };
    case 'all':
      return { start: dayjs('1970-01-01').startOf('day'), end };
    default:
      return getDefaultDateRange(now);
  }
}
